// ---------------------------------------------------------------------------
// Email-link helpers. A work item can carry a single `emailLink` pointing
// back to the email that triggered it. Only three shapes are accepted — the
// same allowlist the backend enforces in WorkItemEmailLink.IsAllowed:
//
//   1. mailto:user@example.com?subject=...
//   2. ms-outlook:... (Outlook desktop deep link)
//   3. https://outlook.office.com/ OR outlook.office365.com OR outlook.live.com
//
// Rejecting arbitrary http/https keeps the field from becoming a phishing
// redirector or a tracker storage surface. The helper is pure, tree-shakable,
// and unit-tested alongside the dashboard derivations.
// ---------------------------------------------------------------------------

const ALLOWED_OUTLOOK_HOSTS = new Set([
  'outlook.office.com',
  'outlook.office365.com',
  'outlook.live.com',
]);

export function isAllowedEmailLink(link: string | undefined | null): boolean {
  if (!link) return false;
  const trimmed = link.trim();
  if (trimmed.length === 0 || trimmed.length > 2000) return false;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('mailto:')) return true;
  if (lower.startsWith('ms-outlook:')) return true;
  if (!lower.startsWith('https://')) return false;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'https:' && ALLOWED_OUTLOOK_HOSTS.has(url.hostname.toLowerCase());
  } catch {
    return false;
  }
}

// Human-friendly label for a stored link. "mailto:a@b" becomes the address
// the user pasted; an https Outlook URL becomes "Open in Outlook on the web";
// an ms-outlook: deep link becomes "Open in Outlook".
export function emailLinkKind(link: string): 'mailto' | 'outlook-desktop' | 'outlook-web' | 'unknown' {
  const lower = link.trim().toLowerCase();
  if (lower.startsWith('mailto:')) return 'mailto';
  if (lower.startsWith('ms-outlook:')) return 'outlook-desktop';
  if (lower.startsWith('https://')) {
    try {
      const url = new URL(link);
      if (ALLOWED_OUTLOOK_HOSTS.has(url.hostname.toLowerCase())) return 'outlook-web';
    } catch {
      return 'unknown';
    }
  }
  return 'unknown';
}

// Hand the link to the OS / browser. For https Outlook URLs we open a new
// tab with noopener+noreferrer. For mailto: and ms-outlook: URIs we navigate
// the current window — the browser will hand off to the OS default handler
// (Outlook desktop on Mac/Windows if configured, Apple Mail otherwise, etc.).
// In neither case do we ever render the URL as raw HTML, so there is no XSS
// surface here.
export function openEmailLink(link: string | undefined | null): boolean {
  if (!isAllowedEmailLink(link)) return false;
  const kind = emailLinkKind(link!);
  if (kind === 'outlook-web') {
    window.open(link!, '_blank', 'noopener,noreferrer');
    return true;
  }
  if (kind === 'mailto' || kind === 'outlook-desktop') {
    // Assigning location triggers the registered protocol handler. We do not
    // want a new tab here because most browsers refuse to open non-http
    // schemes in window.open and fall back to nothing.
    window.location.assign(link!);
    return true;
  }
  return false;
}
